import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, BookOpen, ArrowRight } from 'lucide-react';
import MANUAL from '@/data/manualContent';

export default function HelpPanel({ open, onClose }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Match current path to manual entry (strip detail IDs)
  const baseKey = Object.keys(MANUAL).find(k => {
    if (k === pathname) return true;
    if (k !== '/' && pathname.startsWith(k) && (pathname[k.length] === '/' || pathname.length === k.length)) return true;
    return false;
  }) || '/';

  const page = MANUAL[baseKey];
  if (!open || !page) return null;

  const goTo = (path) => { navigate(path); onClose(); };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/20 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white shadow-2xl border-l border-[#E2E2DF] overflow-y-auto animate-in slide-in-from-right">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-[#E2E2DF] px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen size={18} className="text-[#D35400]" />
            <h2 className="text-lg font-bold text-slate-800">{page.title}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-5 space-y-6">
          {/* Summary */}
          <section>
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#D35400] mb-2">Summary</h3>
            <p className="text-sm text-slate-700 leading-relaxed">{page.summary}</p>
          </section>

          {/* How To */}
          <section>
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#D35400] mb-2">How To Use</h3>
            <ol className="space-y-2">
              {page.howTo.map((step, i) => (
                <li key={i} className="flex gap-2 text-sm text-slate-700">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#D35400]/10 text-[#D35400] text-[10px] font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                  <span className="leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
          </section>

          {/* Detailed Breakdown */}
          <section>
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#D35400] mb-2">Detailed Breakdown</h3>
            <div className="space-y-1.5">
              {page.detailed.split('\n').map((line, i) => {
                const trimmed = line.trim();
                if (!trimmed) return <div key={i} className="h-2" />;
                if (trimmed.match(/^[A-Z ]{4,}[:(]/) || trimmed.match(/^[A-Z ]+:$/)) {
                  return <p key={i} className="text-xs font-bold text-slate-800 mt-2">{trimmed}</p>;
                }
                if (trimmed.startsWith('\u2022') || trimmed.startsWith('[') || trimmed.match(/^\d+\./)) {
                  return <p key={i} className="text-xs text-slate-600 font-mono pl-2 leading-relaxed">{trimmed}</p>;
                }
                if (trimmed.includes('=') || trimmed.includes('\u00d7') || trimmed.includes('\u2192')) {
                  return <p key={i} className="text-xs text-slate-700 font-mono bg-slate-50 rounded px-2 py-1 leading-relaxed">{trimmed}</p>;
                }
                return <p key={i} className="text-sm text-slate-600 leading-relaxed">{trimmed}</p>;
              })}
            </div>
          </section>

          {/* Related Pages */}
          {page.relatedPages && page.relatedPages.length > 0 && (
            <section>
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#D35400] mb-2">Related Pages</h3>
              <div className="flex flex-wrap gap-2">
                {page.relatedPages.map(rp => {
                  const rPage = MANUAL[rp];
                  if (!rPage) return null;
                  return (
                    <button
                      key={rp}
                      onClick={() => goTo(rp)}
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-[#E2E2DF] text-slate-600 hover:border-[#D35400] hover:text-[#D35400] transition-colors"
                    >
                      {rPage.title} <ArrowRight size={11} />
                    </button>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
